import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import '../styles/PaymentRecords.css';
import PaymentModeGauges from './PaymentModeGauges'; // Update path as needed


const PaymentRecords = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: 'paymentDate',
        direction: 'desc'
    });
    const [allBranches, setAllBranches] = useState(['all']);
    const [branchFilter, setBranchFilter] = useState('all');
    const [stats, setStats] = useState({
        totalCount: 0,
        totalAmount: 0,
        paymentTotals: { cash: 0, card: 0, online: 0, cheque: 0, other: 0 },
        paymentCounts: { cash: 0, card: 0, online: 0, cheque: 0, other: 0 },
        paymentModes: {},
        branchTotals: {},
        branchCounts: {},
        bankTotals: {}
    });

    // Date filter states
    const [dateFilterType, setDateFilterType] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // 2. Add pagination logic (add this before the return statement)
    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentInvoices = invoices.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(invoices.length / itemsPerPage);

    // Pagination handlers
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Reset to first page when filters change
    const resetPagination = () => {
        setCurrentPage(1);
    }

    useEffect(() => {
        const today = new Date();
        const formattedToday = today.toISOString().split('T')[0];
        setEndDate(formattedToday);

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);

        fetchInvoices();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch invoices with filters applied
    const fetchInvoices = async () => {
        try {
            setLoading(true);
            resetPagination();

            const token = localStorage.getItem('token');

            if (!token) {
                setError('Authentication token not found. Please login again.');
                setLoading(false);
                return;
            }

            // Build query parameters
            const params = new URLSearchParams({
                searchTerm,
                branchFilter,
                dateFilterType,
                sortKey: sortConfig.key,
                sortDirection: sortConfig.direction,
                includeBranchList: 'true',
                includeAnalytics: 'true' // Request analytics data for graphs
            });

            if (dateFilterType === 'range') {
                params.append('startDate', startDate);
                params.append('endDate', endDate);
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/all-invoices?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setInvoices(response.data.invoices);

            // Log the response to check the data structure
            console.log('API Response:', response.data);

            // Make sure we properly extract and handle payment counts specifically
            // const receivedPaymentCounts = response.data.stats.paymentCounts || {};

            // Only set the initial stats from API
            // Our useEffect will calculate the unique student counts
            setStats({
                totalCount: response.data.stats.totalCount || 0,
                totalAmount: response.data.stats.totalAmount || 0,
                paymentTotals: response.data.stats.paymentTotals || { cash: 0, card: 0, online: 0, cheque: 0, other: 0 },
                paymentCounts: { cash: 0, card: 0, online: 0, cheque: 0, other: 0 }, // We'll calculate these based on unique students
                paymentModes: response.data.stats.paymentModes || {},
                branchTotals: response.data.stats.branchTotals || {},
                branchCounts: {}, // We'll calculate these based on unique students
                bankTotals: response.data.stats.bankTotals || {}
            });

            // Update the branch list if it's included in the response
            if (response.data.availableBranches && response.data.availableBranches.length > 0) {
                setAllBranches(['all', ...response.data.availableBranches]);
            }

            setError(null);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            if (err.response?.status === 401 || err.response?.status === 403) {
                setError('Authentication failed. Please login again.');
            } else {
                setError(err.response?.data?.error || 'Failed to fetch invoice data');
            }
        } finally {
            setLoading(false);
        }
    };

    const getBranchFromStudentId = (studentId) => {
        const branchCodeMap = {
            "TM": "Tambaram",
            "VL": "Velachery",
            "VP": "Vadapalani",
            "PM": "Poonamallee",
            "MH": "Marathahalli",
            "GP": "Gandhipuram",
            "MP": "Malumichampatti",
            "HP": "Hamumanthapuram"
        };

        // Extract branch code from studentId (format includes "-XX-")
        const match = studentId.match(/-([A-Z]{2})-/);
        if (match && match[1]) {
            return branchCodeMap[match[1]] || 'Unknown';
        }
        return 'Unknown';
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });

        // Trigger re-fetch with new sort
        setTimeout(() => fetchInvoices(), 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const applyFilters = () => {
        fetchInvoices();
    };

    // Handle filter changes
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleBranchChange = (e) => {
        setBranchFilter(e.target.value);
    };

    const handleDateFilterChange = (type) => {
        setDateFilterType(type);
    };

    // Custom tooltip for Branch chart to show admission count
    const BranchTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="custom-tooltip">
                    <p className="label">{`${data.name}`}</p>
                    <p className="count">{`No. of Admissions: ${data.count}`}</p>
                    <p className="amount">{`Amount: ${formatCurrency(data.value)}`}</p>
                </div>
            );
        }
        return null;
    };
    // Prepare data for Branch Distribution chart (as BAR CHART)
    const prepareBranchData = () => {
        // Check if branchTotals exists and has data
        if (!stats.branchTotals || Object.keys(stats.branchTotals).length === 0) {
            console.log("No branch data available");
            return [];
        }

        const data = Object.entries(stats.branchTotals)
            .map(([name, value]) => ({
                name,
                value,
                count: stats.branchCounts?.[name] || 0
            }))
            .sort((a, b) => b.count - a.count); // Sort by admission count in descending order

        console.log("Prepared branch data for horizontal bar chart:", data);
        return data;
    };

    // Prepare data for Bank chart (sub-categories)
    const prepareBankData = () => {
        if (!stats.bankTotals || Object.keys(stats.bankTotals).length === 0) {
            console.log("No bank data available");
            return [];
        }

        // Consolidate all cash-like entries into "Cash"
        const consolidatedBankTotals = Object.entries(stats.bankTotals).reduce((acc, [bankName, amount]) => {
            const normalizedBankName = !bankName ||
                bankName.trim() === "" ||
                bankName === "N/A" ||
                bankName.toLowerCase() === "cash"
                ? "Cash"
                : bankName;

            acc[normalizedBankName] = (acc[normalizedBankName] || 0) + amount;
            return acc;
        }, {});

        const data = Object.entries(consolidatedBankTotals)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8); // Take top 8 banks

        console.log("Prepared bank data:", data);
        return data;
    };

    const branchData = prepareBranchData();
    const bankData = prepareBankData();

    // Calculate totals and counts if backend doesn't provide them
    useEffect(() => {
        if (invoices.length > 0) {
            console.log("Calculating accurate admission counts based on unique student IDs");

            // Initialize objects to store counts and totals
            const calculatedPaymentTotals = { cash: 0, card: 0, online: 0, cheque: 0, other: 0 };
            const calculatedBranchTotals = {};
            const calculatedBranchCounts = {};
            const calculatedBankTotals = {};

            // Track unique student IDs per payment method for accurate admission counts
            const uniqueStudentsByPaymentMode = {
                cash: new Set(),
                card: new Set(),
                online: new Set(),
                cheque: new Set(),
                other: new Set()
            };

            // Track unique students by branch
            const uniqueStudentsByBranch = {};

            // Process each invoice
            invoices.forEach(invoice => {
                const studentId = invoice.studentId;
                const branch = getBranchFromStudentId(studentId);
                const amount = Number(invoice.paidAmount || 0);
                const paymentMode = invoice.registrationPaymentMode?.toLowerCase() || 'other';
                const bank = invoice.bank || 'Cash';

                // Initialize branch set if it doesn't exist
                if (!uniqueStudentsByBranch[branch]) {
                    uniqueStudentsByBranch[branch] = new Set();
                }

                // Add student ID to branch set
                uniqueStudentsByBranch[branch].add(studentId);

                // Update branch totals
                calculatedBranchTotals[branch] = (calculatedBranchTotals[branch] || 0) + amount;

                // Update bank totals
                calculatedBankTotals[bank] = (calculatedBankTotals[bank] || 0) + amount;

                // Update payment mode totals and unique student tracking
                if (paymentMode.includes('cash')) {
                    calculatedPaymentTotals.cash += amount;
                    uniqueStudentsByPaymentMode.cash.add(studentId);
                } else if (paymentMode.includes('card')) {
                    calculatedPaymentTotals.card += amount;
                    uniqueStudentsByPaymentMode.card.add(studentId);
                } else if (paymentMode.includes('online') || paymentMode.includes('upi')) {
                    calculatedPaymentTotals.online += amount;
                    uniqueStudentsByPaymentMode.online.add(studentId);
                    console.log("Added online payment for student:", studentId, amount);
                } else if (paymentMode.includes('cheque') || paymentMode.includes('loan')) {
                    calculatedPaymentTotals.cheque += amount;
                    uniqueStudentsByPaymentMode.cheque.add(studentId);
                } else {
                    calculatedPaymentTotals.other += amount;
                    uniqueStudentsByPaymentMode.other.add(studentId);
                }
            });

            // Convert unique student sets to counts
            const calculatedPaymentCounts = {
                cash: uniqueStudentsByPaymentMode.cash.size,
                card: uniqueStudentsByPaymentMode.card.size,
                online: uniqueStudentsByPaymentMode.online.size,
                cheque: uniqueStudentsByPaymentMode.cheque.size,
                other: uniqueStudentsByPaymentMode.other.size
            };

            // Convert unique student branch sets to counts
            Object.keys(uniqueStudentsByBranch).forEach(branch => {
                calculatedBranchCounts[branch] = uniqueStudentsByBranch[branch].size;
            });

            console.log("Unique student admission counts by payment mode:", calculatedPaymentCounts);
            console.log("Unique student admission counts by branch:", calculatedBranchCounts);

            // Update stats with calculated data
            setStats(prevStats => ({
                ...prevStats,
                paymentCounts: {
                    cash: calculatedPaymentCounts.cash,
                    card: calculatedPaymentCounts.card,
                    online: calculatedPaymentCounts.online,
                    cheque: calculatedPaymentCounts.cheque,
                    other: calculatedPaymentCounts.other
                },
                paymentTotals: {
                    cash: calculatedPaymentTotals.cash,
                    card: calculatedPaymentTotals.card,
                    online: calculatedPaymentTotals.online,
                    cheque: calculatedPaymentTotals.cheque,
                    other: calculatedPaymentTotals.other
                },
                branchTotals: calculatedBranchTotals,
                branchCounts: calculatedBranchCounts,
                bankTotals: calculatedBankTotals
            }));
        }
    }, [invoices]);

    return (
        <div className="payment-records-container">
            <h1>Payment Records Dashboard</h1>

            {error && <div className="error-message">{error}</div>}

            <div className="dashboard-controls">
                <div className="search-filter">
                    <input
                        type="text"
                        placeholder="Search by ID, Bank, or Branch..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="searchpr-input"
                    />

                    <select
                        value={branchFilter}
                        onChange={handleBranchChange}
                        className="branchpr-filter"
                    >
                        {allBranches.map(branch => (
                            <option key={branch} value={branch}>
                                {branch === 'all' ? 'All Branches' : branch}
                            </option>
                        ))}
                    </select>

                    <button onClick={applyFilters} className="refresh-btn">
                        Apply Filters
                    </button>
                    <button
                        className="cancel3-btn"
                        onClick={() => window.location.href = '/dashboard'}
                    >
                        Back
                    </button>
                </div>

                {/* Date filter controls */}
                <div className="date-filter">
                    <div className="date-filter-options">
                        <label className={`date-option ${dateFilterType === 'all' ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="dateFilter"
                                checked={dateFilterType === 'all'}
                                onChange={() => handleDateFilterChange('all')}
                            />
                            All Dates
                        </label>
                        <label className={`date-option ${dateFilterType === 'today' ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="dateFilter"
                                checked={dateFilterType === 'today'}
                                onChange={() => handleDateFilterChange('today')}
                            />
                            Today's Payments
                        </label>
                        <label className={`date-option ${dateFilterType === 'range' ? 'active' : ''}`}>
                            <input
                                type="radio"
                                name="dateFilter"
                                checked={dateFilterType === 'range'}
                                onChange={() => handleDateFilterChange('range')}
                            />
                            Date Range
                        </label>
                    </div>

                    {dateFilterType === 'range' && (
                        <div className="date-range-inputs">
                            <div className="date-input-group">
                                <label>From:</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="date-input"
                                />
                            </div>
                            <div className="date-input-group">
                                <label>To:</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="date-input"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="dashboard-stats">
                <div className="stat1-card">
                    <h3>Total Amount</h3>
                    <p>{formatCurrency(stats.totalAmount)}</p>
                </div>

                <div className="stat-card">
                    <h3>Total Payments</h3>
                    <p>{stats.totalCount}</p>
                    <small>No. Admission: {Object.values(stats.paymentCounts).reduce((sum, count) => sum + count, 0)}</small>
                </div>
                <div className="stat-card payment-modes">
                    <h3>Payment Modes</h3>
                    <ul>
                        {Object.entries(stats.paymentModes || {}).map(([mode, count]) => (
                            <li key={mode}>{mode}: {count}</li>
                        ))}
                    </ul>
                </div>
                {/* Payment Mode Gauges */}
                <PaymentModeGauges paymentStats={stats} />

            </div>

            {/* Charts Section */}
            <div className="charts-container">
                {/* Chart 1: Branch-wise Admissions (Bar Chart) */}
                <div className="chart-card region-chart">
                    <h3>Branch-wise Number of Admissions</h3>
                    <div className="chart-container">
                        {branchData && branchData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    layout="vertical"
                                    data={branchData}
                                    margin={{ top: 20, right: 50, left: 60, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                    <XAxis
                                        type="number"
                                        tickFormatter={(value) => {
                                            const maxCount = Math.max(...branchData.map(item => item.count));
                                            return `${Math.round((value / maxCount) * 100)}%`;
                                        }}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        width={90}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <Tooltip content={<BranchTooltip />} />
                                    <Legend />
                                    <Bar
                                        dataKey="count"
                                        name="No. of Admissions"
                                        fill="#0D2B5B"
                                        barSize={30}
                                    >
                                        <LabelList
                                            dataKey="count"
                                            position="right"
                                            formatter={(value) => `${value} admissions`}
                                            style={{ fill: '#333', fontSize: 12 }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="no-data-message">No branch data available</div>
                        )}
                    </div>
                </div>
                {/* Chart 3: Bank-wise Amount (Bar) - Sum of sales by sub-category */}
                <div className="chart-card subcategory-chart">
                    <h3>Bank wise Collections</h3>
                    <div className="chart-container">
                        {bankData && bankData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={bankData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis tickFormatter={(value) => `₹${value / 1000}k`} />
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                    <Legend />
                                    <Bar dataKey="value" name="Amount" fill="#efb102" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="no-data-message">No bank data available</div>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-spinner">Loading...</div>
            ) : (
                <>
                    {invoices.length > 0 ? (
                        <>
                            <div className="table-container">
                                <table className="payment-table">
                                    <thead>
                                        <tr>
                                            <th onClick={() => requestSort('studentId')}>
                                                Student ID {getSortIndicator('studentId')}
                                            </th>
                                            <th>Branch</th>
                                            <th onClick={() => requestSort('paymentInstallment')}>
                                                Installment {getSortIndicator('paymentInstallment')}
                                            </th>
                                            <th onClick={() => requestSort('paymentDate')}>
                                                Payment Date {getSortIndicator('paymentDate')}
                                            </th>
                                            <th onClick={() => requestSort('bank')}>
                                                Bank {getSortIndicator('bank')}
                                            </th>
                                            <th onClick={() => requestSort('paidAmount')}>
                                                Amount {getSortIndicator('paidAmount')}
                                            </th>
                                            <th onClick={() => requestSort('registrationPaymentMode')}>
                                                Payment Mode {getSortIndicator('registrationPaymentMode')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentInvoices.map((invoice, index) => (
                                            <tr key={index}>
                                                <td>{invoice.studentId}</td>
                                                <td>{getBranchFromStudentId(invoice.studentId)}</td>
                                                <td>{invoice.paymentInstallment || 'N/A'}</td>
                                                <td>{formatDate(invoice.paymentDate)}</td>
                                                <td>{!invoice.bank || invoice.bank === 'N/A' ? 'Cash' : invoice.bank}</td>                                                <td className="amount-cell">{formatCurrency(invoice.paidAmount)}</td>
                                                <td>{invoice.registrationPaymentMode || 'N/A'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            <div className="pagination-container">

                                <div className="pagination-controls">
                                    <button
                                        onClick={goToPreviousPage}
                                        disabled={currentPage === 1}
                                        className="pagination-btn"
                                    >
                                        &laquo;

                                    </button>

                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(page => {
                                            // Show first page, last page, current page, and pages around current
                                            return page === 1 ||
                                                page === totalPages ||
                                                (page >= currentPage - 2 && page <= currentPage + 2);
                                        })
                                        .map((page, index, filteredPages) => (
                                            <React.Fragment key={page}>
                                                {/* Add ellipsis if there's a gap */}
                                                {index > 0 && filteredPages[index - 1] < page - 1 && (
                                                    <span className="pagination-ellipsis">...</span>
                                                )}
                                                <button
                                                    onClick={() => paginate(page)}
                                                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                                >
                                                    {page}
                                                </button>
                                            </React.Fragment>
                                        ))
                                    }

                                    <button
                                        onClick={goToNextPage}
                                        disabled={currentPage === totalPages}
                                        className="pagination-btn"
                                    >
                                        &raquo;
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="no-data-message">No payment records found.</div>
                    )}
                </>
            )}
        </div>
    );
};

export default PaymentRecords